import React from 'react';
import { IOrderDetail, IOrderDetailLine } from '../../../actions/order-details/get-order-details.action';
import { DELIVERY_INFORMATION_LEG_STATES, DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS } from '../../../configuration/delivery-information';
import { ILocalizationState } from '../../../states/localization-state';
import { dateLocaleFormatter, getOffsetFormatted, toLocalDate } from '../../../utilities';
import { mapEventNameToText } from '../../../utilities/map-event-name-to-text';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';
import { createStaticLeg } from './delivery-information-modal';

interface EventListProps {
    orderLine: IOrderDetailLine;
    localizationState: ILocalizationState;
    CLASS_NAME: string;
    orderDetails: IOrderDetail;
    resources: IOrderDetailsResources;
}

const EventList: React.FC<EventListProps> = ({ orderLine, localizationState, CLASS_NAME, orderDetails, resources }) => {
    const orderedLegs = orderLine.Legs.map(leg => ({
        ...leg,
        Events: [...leg.Events].sort((a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime())
    }));

    const allEvents = [...orderedLegs].reverse().flatMap(leg =>
        leg.Events.map(event => ({
            ...event,
            LegId: leg.Id,
            LegState: leg.State
        }))
    );

    const completedEvents = allEvents.filter(event => event.LegState === DELIVERY_INFORMATION_LEG_STATES.Completed);

    const activeEvents = allEvents.filter(
        event => event.LegState === DELIVERY_INFORMATION_LEG_STATES.Completed || event.LegState === DELIVERY_INFORMATION_LEG_STATES.Active
    );

    const confirmedLegs = orderLine.Legs.filter(
        leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Confirmed || leg.State === DELIVERY_INFORMATION_LEG_STATES.Planned
    );

    //All events are completed and there is no confirmedleg open
    const isFinalStep = allEvents.length === completedEvents.length && confirmedLegs.length === 0;

    const ordered = createStaticLeg(
        0,
        DELIVERY_INFORMATION_LEG_STATES.Ordered,
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.PARCEL,
        orderDetails.ConfirmedDate
    );

    const completed = createStaticLeg(
        allEvents.length + 1,
        DELIVERY_INFORMATION_LEG_STATES.Completed,
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.FLAG,
        orderLine.ActualArrivalDate ? orderLine.ActualArrivalDate : undefined
    );

    const offsetFormatted = getOffsetFormatted();

    return (
        <ul className={`${CLASS_NAME}-event-list fade-slide-top`}>
            {isFinalStep && (
                <li key={allEvents.length} className={`${CLASS_NAME}-event-list-item completed`}>
                    <span className={`${CLASS_NAME}-event-list-bullet completed checked`} />
                    <span className={`${CLASS_NAME}-event-list-state`}>
                        {(() => {
                            const formattedDate = completed.Events[0].Timestamp
                                ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(completed.Events[0].Timestamp))
                                : '';
                            const onText = formattedDate ? `${resources.orderDetailsModalDeliveryInformationOn}` : '';

                            const html = resources.orderDetailsModalDeliveryInformationCompletedEvent
                                .replace('{name}', `<span style="font-style: italic;">${mapEventNameToText('completed', resources)}</span>`)
                                .replace('{on}', onText)
                                .replace('{date}', formattedDate);

                            return <div dangerouslySetInnerHTML={{ __html: html }} />;
                        })()}
                    </span>
                </li>
            )}
            {activeEvents.map(event => (
                <li key={event.Id} className={`${CLASS_NAME}-event-list-item completed`}>
                    <span className={`${CLASS_NAME}-event-list-bullet completed`} />
                    <span className={`${CLASS_NAME}-event-list-state`}>
                        {(() => {
                            const formattedDate = event.Timestamp
                                ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(event.Timestamp))
                                : '';
                            const formattedTime = event.Timestamp ? dateLocaleFormatter(localizationState).formatTime(event.Timestamp) : '';

                            const onText = formattedDate ? `${resources.orderDetailsModalDeliveryInformationOn}` : '';
                            const inText = event.Location ? `${resources.orderDetailsModalDeliveryInformationIn}` : '';
                            const location = event.Location ? event.Location : '';

                            let htmlContent = resources.orderDetailsModalDeliveryInformationInTransitEvent
                                .replace('{name}', `<span style="font-style: italic;">${mapEventNameToText(event.Name, resources)}</span>`)
                                .replace('{in}', inText)
                                .replace('{location}', location)
                                .replace('{on}', onText)
                                .replace('{date}', `${formattedDate} ${formattedTime} ${offsetFormatted}`);

                            // Remove multiple spaces
                            htmlContent = htmlContent.replace(/\s+/g, ' ').trim();

                            return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
                        })()}
                    </span>
                </li>
            ))}
            <li key={0} className={`${CLASS_NAME}-event-list-item completed ${activeEvents.length === 0 ? 'only-completed' : ''}`}>
                <span className={`${CLASS_NAME}-event-list-bullet completed`} />
                <span className={`${CLASS_NAME}-event-list-state`}>
                    {(() => {
                        const formattedDate = ordered.Events[0].Timestamp
                            ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(ordered.Events[0].Timestamp))
                            : '';
                        const onText = formattedDate ? `${resources.orderDetailsModalDeliveryInformationOn}` : '';

                        const html = resources.orderDetailsModalDeliveryInformationOrderedEvent
                            .replace('{name}', `<span style="font-style: italic;">${mapEventNameToText('ordered', resources)}</span>`)
                            .replace('{on}', onText)
                            .replace('{date}', formattedDate);

                        return <div dangerouslySetInnerHTML={{ __html: html }} />;
                    })()}
                </span>
            </li>
        </ul>
    );
};

export default EventList;
